import { useCallback, useEffect, useState } from 'react';
import { collabApi } from '../api/collab.js';
import { useRealtime } from '../hooks/useRealtime.js';

/**
 * Live activity feed for a single resource. Pulls historical events from
 * /activity and listens to 'activity:logged' on the resource's socket room
 * for real-time updates.
 */
export default function ActivityFeed({ resourceType, resourceId, limit = 20, title = 'Activity' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (!resourceType || !resourceId) return;
    setLoading(true);
    collabApi
      .activity({ resource_type: resourceType, resource_id: resourceId, limit })
      .then((d) => setItems(d?.items || []))
      .finally(() => setLoading(false));
  }, [resourceType, resourceId, limit]);

  useEffect(refresh, [refresh]);

  useRealtime({
    type: resourceType,
    id: resourceId,
    events: {
      'activity:logged': (entry) => {
        setItems((prev) => [{ ...entry, _live: true }, ...prev].slice(0, limit));
      },
    },
  });

  return (
    <div className="activity-feed">
      <h3>{title}</h3>
      {loading && <div className="muted small">Loading…</div>}
      {!loading && items.length === 0 && <div className="muted small">No activity yet.</div>}
      <ol>
        {items.map((a) => (
          <li key={a.id} className={a._live ? 'live' : ''}>
            <div className="dot" />
            <div className="body">
              <div className="row">
                <strong>{a.user_name || 'System'}</strong>
                <span className="muted small">{relTime(a.created_at)}</span>
              </div>
              <div className="muted small">{a.description}</div>
              <div className="muted xs">{a.action_type}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}
